// Phase 7 R4 — Return Parts dialog.
// Records partial or full returns against received PO lines.
// Strict client-side guards: qty > 0, qty <= returnable.
// Pricing is NEVER touched; unit_credit is captured for future credit reconciliation.
import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useRecordPartsReturn } from '@/hooks/purchasing/useRecordPartsReturn';
import { toast } from '@/hooks/use-toast';
import type { PurchaseOrderLine } from '@/hooks/purchasing/usePurchaseOrder';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  shopId: string;
  purchaseOrderId: string;
  poNumber: string;
  lines: PurchaseOrderLine[];
}

interface RowState {
  qty: string;
  reason: string;
}

export function ReturnPartsDialog({
  open,
  onOpenChange,
  shopId,
  purchaseOrderId,
  poNumber,
  lines,
}: Props) {
  const mut = useRecordPartsReturn();
  const [returnRef, setReturnRef] = useState('');
  const [headerReason, setHeaderReason] = useState('');
  const [rows, setRows] = useState<Record<string, RowState>>({});

  const eligible = useMemo(
    () =>
      lines
        .filter(
          (l) =>
            l.status !== 'canceled' &&
            l.status !== 'returned' &&
            Number(l.cumulative_received ?? 0) -
              Number(l.cumulative_returned ?? 0) >
              0,
        )
        .map((l) => ({
          line: l,
          returnable:
            Number(l.cumulative_received ?? 0) -
            Number(l.cumulative_returned ?? 0),
        })),
    [lines],
  );

  const setRow = (id: string, patch: Partial<RowState>) =>
    setRows((prev) => ({
      ...prev,
      [id]: { qty: '', reason: '', ...prev[id], ...patch },
    }));

  const buildPayload = () =>
    eligible
      .map(({ line, returnable }) => {
        const r = rows[line.id];
        const qty = Number(r?.qty ?? 0);
        if (!qty || qty <= 0 || qty > returnable) return null;
        return {
          purchase_order_line_id: line.id,
          quantity_returned: qty,
          reason: r?.reason?.trim() || null,
        };
      })
      .filter((x): x is NonNullable<typeof x> => !!x);

  const payload = buildPayload();
  const canSubmit = payload.length > 0 && !mut.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      await mut.mutateAsync({
        shopId,
        purchaseOrderId,
        return_reference: returnRef.trim() || null,
        reason: headerReason.trim() || null,
        lines: payload,
      });
      toast({
        title: 'Return recorded',
        description: `${payload.length} line${payload.length === 1 ? '' : 's'} returned on ${poNumber}.`,
      });
      setRows({});
      setReturnRef('');
      setHeaderReason('');
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: 'Return failed',
        description: e?.message ?? 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Return parts on {poNumber}</DialogTitle>
          <DialogDescription>
            Enter the quantity to return for each line. Returns cannot exceed the
            quantity already received.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="return-ref">Reference / RMA #</Label>
            <Input
              id="return-ref"
              value={returnRef}
              onChange={(e) => setReturnRef(e.target.value)}
              placeholder="e.g. RMA-12345"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="return-reason">Reason</Label>
            <Input
              id="return-reason"
              value={headerReason}
              onChange={(e) => setHeaderReason(e.target.value)}
              placeholder="e.g. Wrong part shipped"
            />
          </div>
        </div>

        {eligible.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
            No lines are eligible for return.
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Received</TableHead>
                  <TableHead className="text-right">Returned</TableHead>
                  <TableHead className="text-right">Returnable</TableHead>
                  <TableHead className="text-right w-[110px]">Return now</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eligible.map(({ line, returnable }) => {
                  const row = rows[line.id] ?? { qty: '', reason: '' };
                  const qtyNum = Number(row.qty);
                  const invalid =
                    row.qty !== '' && (qtyNum <= 0 || qtyNum > returnable);
                  return (
                    <TableRow key={line.id}>
                      <TableCell>
                        <div className="font-medium">{line.description}</div>
                        {line.vendor_part_number && (
                          <div className="text-xs text-muted-foreground">
                            {line.vendor_part_number}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(line.cumulative_received ?? 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {Number(line.cumulative_returned ?? 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">
                        {returnable}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min={0}
                          max={returnable}
                          step="1"
                          value={row.qty}
                          onChange={(e) => setRow(line.id, { qty: e.target.value })}
                          className={
                            invalid ? 'border-destructive focus-visible:ring-destructive' : ''
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Textarea
                          rows={1}
                          value={row.reason}
                          onChange={(e) => setRow(line.id, { reason: e.target.value })}
                          placeholder="Optional"
                          className="min-h-[40px] resize-none"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            Record return ({payload.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
